import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { useProjects } from '@/hooks/use-projects';
import { resolveAllProjectsPath, resolveProjectSwitchPath } from '@/lib/project-routes';
import { cn } from '@/lib/utils';
import { useCurrentProjectStore } from '@/stores/current-project';
import { Check, ChevronsUpDown, Layers, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Scope selector for the page below. On the table lists it heads the filter
 * row, immediately before the search field; on the other project sub-pages it
 * sits in the page band before that page's own action (§24).
 *
 * Shaped as an outlined button on the controls' height: a letter square, the
 * name, and a chevron that says "this opens". It reads as a filter of what the
 * table holds, not as a second page action.
 *
 * Choosing a project re-points the page in front of the reader and nothing
 * else — a project sub-page keeps its section, the two lists take the new
 * `?project=` and keep their filters (`lib/project-routes.ts`). It never
 * navigates away from the page whose band it sits in.
 *
 * The rail used to carry a second trigger for this same panel, at the head of
 * its project group. It went: the band already names the project on every
 * scoped page, so the row was a second control for what the reader was looking
 * at (spec §28).
 *
 * On the two lists it also carries the *other* half of the scope: "All
 * projects" is the last row in this panel rather than a segmented control
 * beside it (spec §45). The list and the picker were two controls on one axis —
 * which projects the table below holds — and the pair read as "Bordeaux/Boracay,
 * or everything" only by accident of being adjacent. Here the trigger names what
 * the page is actually scoped to, and the row that widens the list sits at the
 * end of the rows that narrow it: the list is a column from "all of them" to one
 * of them, read top to bottom.
 */
export function ProjectSwitcher({ allProjects = false }: { allProjects?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: projects = [] } = useProjects();
  const currentId = useCurrentProjectStore((s) => s.currentProjectId);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const current = useMemo(
    () => projects.find((p) => p.public_id === currentId || p.id === currentId) ?? null,
    [projects, currentId],
  );

  // The list states its scope in the URL: `?project=` is one project, its
  // absence is every project the reader can see. Only the two lists can be
  // widened like that, so only they offer the row (and only they resolve the
  // trigger this way — a project sub-page is always its own project's).
  const allScope = allProjects && !new URLSearchParams(location.search).get('project');

  const filtered = useMemo(() => {
    if (!search) return projects;
    const q = search.toLowerCase();
    return projects.filter((p) => p.name.toLowerCase().includes(q) || p.public_id.includes(q));
  }, [projects, search]);

  // No project to choose from means no control: a button that opens an empty
  // panel is a dead end, and the page below already says what to do about it.
  if (projects.length === 0) return null;

  const select = (publicId: string) => {
    setOpen(false);
    setSearch('');
    navigate(resolveProjectSwitchPath(location.pathname, location.search, publicId));
  };

  const selectAll = () => {
    setOpen(false);
    setSearch('');
    navigate(resolveAllProjectsPath(location.pathname, location.search));
  };

  // What the page below is scoped to: the trigger's label, and — with the `#id`
  // the visible label has no room for — its accessible name.
  const named = current ? `${current.name} #${current.public_id}` : '';
  const scopeLabel = allScope
    ? t('nav.all_projects', 'All projects')
    : (current?.name ?? t('nav.select_project', 'Select a project'));
  const scopeSpoken = allScope ? t('nav.all_projects', 'All projects') : named;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="max-w-[13rem] justify-start gap-2 ps-1.5"
          aria-label={
            scopeSpoken ? `${t('nav.switch_project')}: ${scopeSpoken}` : t('nav.switch_project')
          }
          title={scopeSpoken || undefined}
        >
          <span className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-surface-2 text-2xs font-semibold text-foreground">
            {allScope ? (
              <Layers aria-hidden className="size-3" />
            ) : (
              (current?.name?.[0]?.toUpperCase() ?? '?')
            )}
          </span>
          <span className="truncate">{scopeLabel}</span>
          <ChevronsUpDown aria-hidden className="ms-0.5 size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="min-w-64 rounded-lg p-2">
        {projects.length >= 8 && (
          <div className="relative mb-2">
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-subtle-foreground"
            />
            <Input
              autoFocus
              placeholder={t('nav.search_projects')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 pl-7 text-xs"
            />
          </div>
        )}
        <div className="custom-scroll max-h-80 overflow-y-auto">
          {filtered.map((p) => {
            // In the cross-project listing no project is the scope, so no row
            // is ticked: the tick means "this is what the page shows".
            const isCurrent = !allScope && p.id === current?.id;
            return (
              <button
                key={p.id}
                type="button"
                aria-current={isCurrent ? 'true' : undefined}
                onClick={() => select(p.public_id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2.5 py-2 text-left transition-colors hover:bg-accent',
                  isCurrent && 'bg-surface-2',
                )}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm text-foreground">{p.name}</span>
                  <span className="truncate mono text-2xs text-muted-foreground">
                    #{p.public_id}
                  </span>
                </span>
                {isCurrent && <Check aria-hidden className="size-3.5 text-brand" />}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-2.5 py-3 text-sm text-muted-foreground">
              {t('nav.no_projects_match')}
            </div>
          )}
          {allProjects && (
            /* The last option, and the only one a filter cannot hide: this row is
               a scope, not a project — "Boracay" is a name nobody can type. It
               closes the list as its widest reading, and the whole block sticks
               to the bottom of the scrollport so a long project list never hides
               the way back out. */
            <div className="sticky bottom-0 bg-popover">
              <Separator className="my-1.5" />
              <button
                type="button"
                aria-current={allScope ? 'true' : undefined}
                onClick={selectAll}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2.5 py-2 text-left transition-colors hover:bg-accent',
                  allScope && 'bg-surface-2',
                )}
              >
                <Layers aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-sm text-foreground">
                  {t('nav.all_projects', 'All projects')}
                </span>
                {allScope && <Check aria-hidden className="size-3.5 text-brand" />}
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
